import { FORM_ERRORS_TEXT } from "@/utils/constants";
import { parse, format, parseISO, isValid } from "date-fns";
import { useOrderInfoStore } from "@/stores/orderInfoStore";
import { storeToRefs } from "pinia";
import axios from "axios";
import { validationErrorMessages } from "@/utils/validationErrorMessages";

export function hasDuplicate(items, newItem) {
  return items.some((item) => item.label === newItem.label);
}

export function convertDateFormat(date) {
  if (!date) {
    return "";
  }
  const dateWithoutTime = date.substring(0, 10);

  return format(parse(dateWithoutTime, "yyyy-MM-dd", new Date()), "MMM dd, yyyy");
}

/** Date half of a timestamp in the browser's zone, so it agrees with `formatTimePart`. */
export function formatDatePart(date) {
  const d = date ? parseISO(date) : null;
  return d && isValid(d) ? format(d, "MMM dd, yyyy") : "";
}

export function formatTimePart(date) {
  const d = date ? parseISO(date) : null;
  return d && isValid(d) ? format(d, "h:mm a") : "";
}

/** Absolute timestamp for log-note displays, e.g. `Jun 24, 2026 · 1:30 PM`. */
export function formatNoteTimestamp(value) {
  if (!value) return "";
  const d = typeof value === "string" ? parseISO(value) : new Date(value);
  return isValid(d) ? format(d, "MMM d, yyyy · h:mm a") : "";
}

export function convertDateToMMDDYYYY(date) {
  if (!date) {
    return "";
  }
  const dateWithoutTime = date.substring(0, 10);

  return format(parse(dateWithoutTime, "yyyy-MM-dd", new Date()), "MM/dd/yyyy");
}

export function titleCaseMixedString(str) {
  if (!str) {
    return "";
  }
  return str
    .replace(/_/g, " ")
    .replace(/([A-Z])/g, " $1")
    .replace(/([0-9])/g, " $1")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function titleCaseString(str) {
  if (!str) {
    return "";
  }
  return str
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function convertDateToYYYYMMDD(date) {
  if (!date) {
    return "";
  }
  // array of possible date formats
  const dateFormats = ["yyyy-MM-dd", "MMM dd, yyyy", "MM/dd/yyyy", "MM-dd-yyyy", "MM.dd.yyyy", "MM dd yyyy"];
  // loop through array of possible date formats and return the first one that parses correctly
  for (let i = 0; i < dateFormats.length; i++) {
    const parsedDate = parse(date, dateFormats[i], new Date());
    if (parsedDate.toString() !== "Invalid Date") {
      return format(parsedDate, "yyyy-MM-dd");
    }
  }
}

export function formatDateToString(date) {
  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) return null;
  return [
    dateObj.getFullYear(),
    (dateObj.getMonth() + 1).toString().padStart(2, "0"),
    dateObj.getDate().toString().padStart(2, "0"),
  ].join("-");
}

export function formatDateISO(d) {
  if (!d) return undefined;
  if (d instanceof Date) return formatDateToString(d);
  return d;
}

export function generateRandom4DigitNumber() {
  return Math.floor(Math.random() * (9999 - 1000 + 1)) + 1000;
}

export function sendFormByEnterClicking(event, sendRequest) {
  if (event.code === "Enter" || event.code === 76) {
    sendRequest();
  }
}

/**
 * Parses an API error response into a list of user-displayable messages.
 *
 * Priority chain (first match wins):
 *   1. validation_errors (DRF/pydantic ValidationError field errors)
 *   2. error_details (PortalException canonical message) — unless it's the
 *      "Validation failed" shim or a 5xx (suppressed to avoid leaking
 *      exception class names from un-adapted exceptions)
 *   3. source_error (legacy PortalException debug/source info, coerced)
 *   4. error (defensive fallback for legacy raw `{"error": "..."}` shapes)
 *   5. detail (DRF default for auth/permission/throttle)
 *   6. non_field_errors (DRF non-field validation)
 *   - Network failures (no response object) → connection/timeout messages
 *   - Final fallback → UNKNOWN_SERVER_ERROR
 *
 * Note: error responses are NOT camelized (see api.js:50 — only success
 * responses are camelized), so we read snake_case keys directly.
 */
export function tryParseApiErrors(error) {
  // Network failure / CORS / no response object
  const data = error?.response?.data;
  if (!data) {
    if (error?.code === "ECONNABORTED" || /timeout/i.test(error?.message || "")) {
      return [FORM_ERRORS_TEXT.NETWORK_TIMEOUT];
    }
    return [FORM_ERRORS_TEXT.NETWORK_ERROR];
  }

  const status = error.response.status;

  // 1. Field-level validation errors
  if (data.validation_errors && typeof data.validation_errors === "object") {
    const messages = [];
    for (const key in data.validation_errors) {
      messages.push(...validationErrorMessages(data.validation_errors[key]));
    }
    if (messages.length) return messages;
  }

  // 5xx safety rail: never leak exception class names / internal messages
  if (status >= 500) {
    return [FORM_ERRORS_TEXT.UNKNOWN_SERVER_ERROR];
  }

  // 2. PortalException canonical message (skip the "Validation failed" shim)
  if (data.error_details && data.error_details !== "Validation failed") {
    return [String(data.error_details)];
  }

  // 3. Legacy source_error (coerce non-strings to avoid [object Object])
  if (data.source_error) {
    return [typeof data.source_error === "string" ? data.source_error : JSON.stringify(data.source_error)];
  }

  // 4. Legacy raw { error: "..." } shape
  if (data.error) {
    return [String(data.error)];
  }

  // 5. DRF default (NotAuthenticated, PermissionDenied, Throttled, etc.)
  if (data.detail) {
    return [String(data.detail)];
  }

  // 6. DRF non_field_errors
  if (Array.isArray(data.non_field_errors) && data.non_field_errors.length) {
    return data.non_field_errors.map(String);
  }

  return [FORM_ERRORS_TEXT.UNKNOWN_SERVER_ERROR];
}

/**
 * Display name for a record-lock holder. Accepts both camelCase (peek GET, camelized
 * by the interceptor) and snake_case (423 error bodies, which bypass camelization).
 */
export function formatLockHolderName(user) {
  if (!user) return "";
  const name = `${user.firstName ?? user.first_name ?? ""} ${user.lastName ?? user.last_name ?? ""}`.trim();
  return name || user.email || "";
}

export function clearOrderInfoStorage() {
  const { orderInfo } = storeToRefs(useOrderInfoStore());
  orderInfo.value.patientPrefix = null;
  orderInfo.value.patientFirstName = null;
  orderInfo.value.patientMiddleName = null;
  orderInfo.value.patientLastName = null;
  orderInfo.value.patientSuffix = null;
  orderInfo.value.patientDob = null;
  orderInfo.value.patientMemberId = null;
  orderInfo.value.patientPhone = null;
  orderInfo.value.patientGender = null;
  orderInfo.value.patientAddress1 = null;
  orderInfo.value.patientAddress2 = null;
  orderInfo.value.patientCity = null;
  orderInfo.value.patientState = null;
  orderInfo.value.patientZip = null;
  orderInfo.value.patientWeight = null;
  orderInfo.value.patientHeight = null;
  orderInfo.value.patientDrugAllergies = null;
  orderInfo.value.patientExternalId = null;
  // Cleared alongside patientExternalId: this store is shared with AllFormStepsView, so a
  // previously VIEWED order's extra patient charts would otherwise survive an in-SPA hop to
  // New Submission and file the next patient's PA under the last one's MRNs (LAM-4951).
  orderInfo.value.additionalPatientExternalIds = [];
  // Same shared-store reason: a viewed order's DOA attestation must not pre-tick the next new PA (LAM-5919).
  orderInfo.value.prescriberDoaAttested = false;
  orderInfo.value.patientSsn = null;
  orderInfo.value.patientInsuranceMemberName = null;
  // Cleared for the same reason as additionalPatientExternalIds above: the review pages assign a
  // whole fetched order onto this shared store, and nothing on the New Submission form can
  // overwrite this field — there is no input for it, and the insurance-card picker deliberately
  // does not write it (see INSURANCE_CARD_TO_ORDER_FIELD). Left behind, the viewed order's
  // insurance state is saved onto the NEXT patient's PA and filed onto their insurance card.
  orderInfo.value.patientInsuranceState = null;
  orderInfo.value.providerFirstName = null;
  orderInfo.value.providerLastName = null;
  orderInfo.value.providerTitle = null;
  orderInfo.value.providerContactName = null;
  orderInfo.value.providerContactPhone = null;
  orderInfo.value.providerContactFax = null;
  orderInfo.value.providerNpiNumber = null;
  orderInfo.value.providerTin = null;
  orderInfo.value.providerSpecialty = null;
  orderInfo.value.providerOfficeName = null;
  orderInfo.value.providerOfficePhone = null;
  orderInfo.value.providerFax = null;
  orderInfo.value.providerEmail = null;
  orderInfo.value.providerAddress1 = null;
  orderInfo.value.providerAddress2 = null;
  orderInfo.value.providerCity = null;
  orderInfo.value.providerState = null;
  orderInfo.value.providerZip = null;
  orderInfo.value.providerDeaNumber = null;
  orderInfo.value.providerLicenseNumber = null;
  orderInfo.value.primaryDiagnosisName = null;
  orderInfo.value.primaryDiagnosisIcd10Code = null;
  orderInfo.value.secondaryDiagnosis = [
    {
      name: null,
      icd10Code: null,
    },
  ];
  orderInfo.value.medication = null;
  orderInfo.value.nationalDrugCode = null;
  orderInfo.value.prescriptionNumber = null;
  orderInfo.value.dailyDosage = null;
  orderInfo.value.daw = "No";
  orderInfo.value.frequency = null;
  orderInfo.value.expectedLengthOfTherapy = null;
  orderInfo.value.quantityPer30Days = null;
  orderInfo.value.packageSizeUnit = null;
  orderInfo.value.treatmentStartDate = null;
  orderInfo.value.expeditedReviewStatement = null;
  orderInfo.value.expeditedReviewWithin24h = null;
  orderInfo.value.checklist = [];
  orderInfo.value.checklistLlmScores = [];
  orderInfo.value.basicPlusChecklist = [];
  orderInfo.value.basicPlusChecklistLlmScores = [];
  orderInfo.value.insuranceProvider = null;
  orderInfo.value.insurancePlanType = null;
  orderInfo.value.benefitType = null;
  orderInfo.value.medicalNecessity = [];
  orderInfo.value.patientChart = [];
  orderInfo.value.aiReferenceDocuments = [];
  orderInfo.value.removedAiReferenceLinks = [];
  orderInfo.value.orderKey = null;
  orderInfo.value.formStep = null;
  orderInfo.value.status = null; // Status is backend-driven; clear on reset
  orderInfo.value.payerQuestions = [];
  orderInfo.value.basicPlusQuestions = [];
  orderInfo.value.medicalNecessityTemplate = null;
  orderInfo.value.user = null;
  orderInfo.value.createdOn = null;
  orderInfo.value.modifiedOn = null;
  orderInfo.value.signature = null;
  orderInfo.value.stageOfTherapy = null;
  orderInfo.value.pharmacyInfo = null;
  orderInfo.value.bin = null;
  orderInfo.value.processorControlNumber = null;
  orderInfo.value.grpNumber = null;
  orderInfo.value.faxDescription = null;
  orderInfo.value.submissionMethod = null;
  orderInfo.value.orderSubmission = null;
  orderInfo.value.extraMedications = [];
  orderInfo.value.draftAdditionalMedications = [];
  orderInfo.value.editableSubmissionForm = {};
  orderInfo.value.filledFormUrl = null;
  orderInfo.value.sendFaxFormTo = null;
  orderInfo.value.faxSubmissionFormDetail = null;
}

export const formatFileSize = (bytes) => {
  if (bytes === null || bytes === undefined) return "";
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

export const pluralize = (count, singular, plural) => {
  if (plural === undefined) {
    plural = singular + "s";
  }
  return count === 1 ? singular : plural;
};

// The only segment widths a dashed NDC is ever printed in: the 11-digit 5-4-2 form
// plus the three 10-digit forms, each of which pads a different segment to reach it.
// Mirrors _PADDABLE_NDC_SEGMENT_WIDTHS in backend/portal/utils/common.py.
const PADDABLE_NDC_SEGMENT_WIDTHS = new Set(["5,4,2", "4,4,2", "5,3,2", "5,4,1"]);

// Normalize a user-entered NDC to the stored 11-digit (5-4-2) form.
// Dashed input is padded segment-wise, since the segment widths are what
// identify a 10-digit NDC as 4-4-2, 5-3-2 or 5-4-1. Bare 10-digit input is
// ambiguous between those three, so it is left alone for validation to reject
// rather than guessed at. Widths outside the four real layouts are not NDCs at
// all — padding them would invent a valid-looking code for the wrong drug — so
// they fall through digits-only. Anything unrecognized is returned digits-only.
export const normalizeNdc = (value) => {
  if (!value) return value;

  const trimmed = String(value).trim();
  if (!trimmed) return trimmed;

  const digitsOnly = trimmed.replace(/\D/g, "");

  const segments = trimmed.split("-");
  if (segments.length === 3 && segments.every((segment) => /^\d+$/.test(segment))) {
    const [labeler, product, pkg] = segments;
    const widths = [labeler.length, product.length, pkg.length].join(",");
    if (PADDABLE_NDC_SEGMENT_WIDTHS.has(widths)) {
      return labeler.padStart(5, "0") + product.padStart(4, "0") + pkg.padStart(2, "0");
    }
  }

  return digitsOnly;
};

export const isValidNdc = (value) => /^\d{11}$/.test(normalizeNdc(value) ?? "");

const presentParts = (...values) => values.map((v) => (typeof v === "string" ? v.trim() : v)).filter(Boolean);

/** "street, line 2, city, state zip" for a patient record; legacy rosters keep parts in extraData. */
export const formatPatientAddress = (patient) => {
  if (!patient) return "";
  const extra = patient.extraData ?? {};
  const street = presentParts(patient.address || extra.patientAddress1, extra.patientAddress2).join(", ");
  const region = presentParts(
    patient.city || extra.patientCity,
    presentParts(patient.state || extra.patientState, patient.zip || extra.patientZip).join(" ")
  ).join(", ");
  return presentParts(street, region).join(", ");
};

export const formatContactNumber = (number) => {
  if (!number) {
    return "";
  }
  return number.replace(/(\d{3}).*?(\d{3}).*?(\d{1,})/, "$1-$2-$3");
};

// Format phone number for display - supports international formats
export const formatPhoneNumber = (value) => {
  if (!value) return "";

  // Check if it starts with + (international format)
  const hasCountryCode = value.trim().startsWith("+");

  // Extract digits only
  const digits = value.replace(/\D/g, "");

  if (!digits) return hasCountryCode ? "+" : "";

  if (hasCountryCode) {
    // International format: +X XXX XXX XXXX or +XX XXX XXX XXXX
    if (digits.length <= 3) {
      return `+${digits}`;
    } else if (digits.length <= 6) {
      return `+${digits.slice(0, -3)} ${digits.slice(-3)}`;
    } else if (digits.length <= 10) {
      return `+${digits.slice(0, -6)} ${digits.slice(-6, -3)} ${digits.slice(-3)}`;
    } else {
      // 11+ digits: +X (XXX) XXX-XXXX for US/Canada style
      const countryCode = digits.slice(0, digits.length - 10);
      const rest = digits.slice(-10);
      return `+${countryCode} (${rest.slice(0, 3)}) ${rest.slice(3, 6)}-${rest.slice(6)}`;
    }
  } else {
    // No country code - assume US/Canada format for 10 digits
    if (digits.length <= 3) {
      return digits;
    } else if (digits.length <= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else if (digits.length <= 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    } else {
      // More than 10 digits without +, treat first digits as country code
      const countryCode = digits.slice(0, digits.length - 10);
      const rest = digits.slice(-10);
      return `+${countryCode} (${rest.slice(0, 3)}) ${rest.slice(3, 6)}-${rest.slice(6)}`;
    }
  }
};

export const timeAgoString = (date, now = new Date()) => {
  const msAgo = now - date;
  const secondsAgo = msAgo / 1000;
  const minutesAgo = secondsAgo / 60;
  const hoursAgo = minutesAgo / 60;
  const daysAgo = hoursAgo / 24;
  const monthsAgo = daysAgo / 30;
  const yearsAgo = monthsAgo / 12;
  if (yearsAgo >= 1) {
    return `${Math.round(yearsAgo)} ${pluralize(Math.round(yearsAgo), "year")}`;
  }
  if (monthsAgo >= 1) {
    return `${Math.round(monthsAgo)} ${pluralize(Math.round(monthsAgo), "month")}`;
  }
  if (daysAgo >= 1) {
    return `${Math.round(daysAgo)} ${pluralize(Math.round(daysAgo), "day")}`;
  }
  if (hoursAgo >= 1) {
    return `${Math.round(hoursAgo)} ${pluralize(Math.round(hoursAgo), "hour")}`;
  }
  return `${Math.round(minutesAgo)} ${pluralize(Math.round(minutesAgo), "minute")}`;
};

/**
 * Fetches diagnosis name from ICD10 code using NLM API
 * @param {string} icd10Code - The ICD10 code to look up
 * @returns {Promise<string|null>} The diagnosis name or null if not found
 */
export const fetchDiagnosisNameFromICD10 = async (icd10Code) => {
  if (!icd10Code) return null;
  try {
    const url = `https://clinicaltables.nlm.nih.gov/api/icd10cm/v3/search?sf=code,name&df=code,name&terms=${encodeURIComponent(
      icd10Code
    )}`;
    const result = await axios.get(url).then((res) => res.data);
    if (result && result[3] && result[3].length > 0) {
      // Find exact match for the code
      const match = result[3].find((item) => item[0] === icd10Code);
      if (match) {
        return match[1]; // Return diagnosis name
      }
      // If no exact match, return first result
      return result[3][0][1];
    }
    return null;
  } catch (error) {
    return null;
  }
};
